import {
  useCreateCorrespondenceThreadMutation,
  getFilteredCorrespondence,
  getFullName,
  CorrespondenceEntityName,
  useMarkCorrespondencesAsReadMutation,
} from "toptaal-shared"
import { correspondenceQueryKeys } from "toptaal-shared"
import {
  createFileRoute,
  useNavigate,
  useParams,
  useRouter,
  useSearch,
} from "@tanstack/react-router"
import {
  CorrespondenceEntityType,
  CorrespondenceOverview,
  CorrespondenceThread,
  useCorrespondenceRouteWithUrlState,
  createTableSearchSchema,
} from "toptaal-shared"
import { can } from "@/utils/can"
import { checkPermissions } from "@/utils/checkPermissions"
import { useCurrentUserSuspenseQuery } from "@/api/queries/useCurrentUserSuspenseQuery"
import { useStudentSuspenseQuery } from "@/api/queries/students/useStudentSuspenseQuery"

export const Route = createFileRoute(
  "/beheer/cursisten/$studentId/correspondentie/",
)({
  component: RouteComponent,
  validateSearch: createTableSearchSchema(),
  beforeLoad: async () => {
    return checkPermissions("read", "studentCorrespondence")
  },
})

function RouteComponent() {
  const { studentId } = useParams({
    from: "/beheer/cursisten/$studentId/correspondentie/",
  })
  const searchParams = useSearch({
    from: "/beheer/cursisten/$studentId/correspondentie/",
  })
  const navigate = useNavigate({
    from: "/beheer/cursisten/$studentId/correspondentie/",
  })

  const router = useRouter()
  const { data: user } = useCurrentUserSuspenseQuery()

  const {
    data: { data: student },
  } = useStudentSuspenseQuery(Number(studentId))

  const fullName = getFullName({
    firstName: student.firstName,
    lastName: student.lastName,
  })

  const entityType: CorrespondenceEntityType = "Student"
  const entityId = Number(studentId)
  const entityName: CorrespondenceEntityName = fullName

  const correspondenceProps = useCorrespondenceRouteWithUrlState({
    entityType,
    entityId,
    queryKey: correspondenceQueryKeys.student(entityId, entityType),
    queryFn: (filters) =>
      getFilteredCorrespondence({ entityId, entityType, filters }),
    useCreateMutation: useCreateCorrespondenceThreadMutation,
    searchParams,
    navigate,
    storageKey: `/beheer/cursisten/${studentId}/correspondentie`,
  })

  const markAsReadMutation = useMarkCorrespondencesAsReadMutation({
    queryKey: correspondenceQueryKeys.student(entityId, entityType),
  })

  const handleNavigateToThread = (thread: CorrespondenceThread) => {
    if (thread.unreadCount > 0) {
      markAsReadMutation.mutate([thread.id])
    }

    router.navigate({
      to: "/beheer/cursisten/$studentId/correspondentie/$threadId",
      replace: true,
      params: {
        threadId: String(thread.id),
        studentId,
      },
    })
  }

  return (
    <CorrespondenceOverview
      {...correspondenceProps}
      handleNavigateToThread={handleNavigateToThread}
      contextType={entityType}
      entityName={entityName}
      canCreateCorrespondence={can("create", "studentCorrespondence")}
      correspondencePermission={{
        root: "mtt",
        roles: user.data.roles,
        baseUserId: user.data.baseUserId,
      }}
    />
  )
}
